import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Chip, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem } from '@heroui/react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { FaPlus, FaTiktok, FaInstagram } from 'react-icons/fa6';
import { FaEllipsisVertical, FaArrowUpRightFromSquare, FaLinkSlash, FaRotate, FaTriangleExclamation } from 'react-icons/fa6';
import repositories from '../../repositories.jsx';
import { CONNECT_ERRORS, gradFor } from '../../services/autoDub/mappers.js';
import Header from '../../components/Header.jsx';
import PageLoader from '../../components/PageLoader.jsx';
import ChannelAvatar from '../../components/autoDub/primitives/ChannelAvatar.jsx';
import YouTubeBadge from '../../components/autoDub/primitives/YouTubeBadge.jsx';

/* ---- Gradient command banner — overview + primary action ---- */
function CommandBanner({ count, onConnect, connecting }) {
	return (
		<motion.div
			initial={{ opacity: 0, y: -12 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ type: 'spring', stiffness: 220, damping: 26 }}
			className="relative overflow-hidden rounded-3xl px-6 py-5 md:px-7 md:py-6 mb-6 text-white bg-gradient-to-br from-primary via-secondary to-fuchsia-600"
		>
			{/* aurora glow */}
			<motion.span aria-hidden className="pointer-events-none absolute -top-20 -right-10 h-64 w-64 rounded-full bg-white/20 blur-3xl"
				animate={{ x: [0, -20, 0], y: [0, 16, 0] }} transition={{ duration: 13, repeat: Infinity, ease: 'easeInOut' }} />
			<motion.span aria-hidden className="pointer-events-none absolute -bottom-24 left-1/3 h-64 w-64 rounded-full bg-fuchsia-300/25 blur-3xl"
				animate={{ x: [0, 24, 0], y: [0, -14, 0] }} transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }} />
			{/* subtle grid texture */}
			<span aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.12] bg-[linear-gradient(to_right,rgba(255,255,255,.6)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,.6)_1px,transparent_1px)] [background-size:26px_26px]" />

			<div className="relative flex flex-col md:flex-row md:items-center justify-between gap-4">
				<div>
					<div className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.14em] uppercase text-white/80 mb-1.5">
						<YouTubeBadge size={18} /> YouTube automation
					</div>
					<h1 className="m-0 text-[24px] md:text-[27px] font-bold tracking-tight leading-tight">
						<span className="tabular-nums">{count}</span> {count === 1 ? 'channel' : 'channels'} connected
					</h1>
					<p className="mt-1 text-[13.5px] text-white/80 max-w-lg leading-snug">
						Manage the YouTube channels AudioDub can read from and publish to. Connect more anytime.
					</p>
				</div>

				<Button
					size="lg"
					radius="full"
					className="bg-white text-primary font-semibold shadow-lg shadow-black/10 hover:scale-[1.02] transition-transform self-start md:self-auto"
					isLoading={connecting}
					startContent={!connecting && <FaPlus size={15} />}
					onPress={onConnect}
				>
					{count > 0 ? 'Connect another' : 'Connect channel'}
				</Button>
			</div>
		</motion.div>
	);
}

/* ---- Providers strip — current + upcoming integrations ---- */
function ProvidersStrip({ count }) {
	const providers = [
		{ key: 'yt', name: 'YouTube', status: `${count} connected`, ok: true, badge: <YouTubeBadge size={26} /> },
		{ key: 'tt', name: 'TikTok', status: 'Coming soon', ok: false, badge: <div className="w-[26px] h-[26px] rounded-lg grid place-items-center bg-black text-white"><FaTiktok size={15} /></div> },
		{ key: 'ig', name: 'Instagram', status: 'Coming soon', ok: false, badge: <div className="w-[26px] h-[26px] rounded-lg grid place-items-center text-white" style={{ background: 'linear-gradient(135deg,#F58529,#DD2A7B,#8134AF)' }}><FaInstagram size={16} /></div> },
	];
	return (
		<div className="flex gap-2.5 flex-wrap mb-6">
			{providers.map((p, i) => (
				<motion.div
					key={p.key}
					initial={{ opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.1 + i * 0.05 }}
					className={`relative overflow-hidden rounded-2xl border px-4 py-3 flex items-center gap-3 backdrop-blur transition-colors
						${p.ok ? 'border-emerald-200 bg-white' : 'border-slate-200 bg-white/70'}`}
				>
					{p.ok && <span aria-hidden className="absolute -right-6 -top-6 h-16 w-16 rounded-full bg-emerald-400/10 blur-xl" />}
					<span className={p.ok ? '' : 'opacity-60'}>{p.badge}</span>
					<div>
						<div className="text-[13.5px] font-semibold text-slate-800">{p.name}</div>
						<div className={`text-[11.5px] font-semibold flex items-center gap-1 ${p.ok ? 'text-emerald-500' : 'text-slate-400'}`}>
							{p.ok && <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />}
							{p.status}
						</div>
					</div>
				</motion.div>
			))}
		</div>
	);
}

/* ---- Connected channel card ---- */
function ConnectedChannelCard({ ch, onDisconnect, disconnecting, onReconnect, reconnecting, index }) {
	const grad = gradFor(ch.platform_channel_id || ch.id);
	const youtubeUrl = ch.platform_channel_id ? `https://www.youtube.com/channel/${ch.platform_channel_id}` : null;
	const connected = (ch.status || 'connected') === 'connected';
	const needsReconnect = ch.needs_reconnect || !connected;

	return (
		<motion.div
			initial={{ opacity: 0, y: 18 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ delay: index * 0.05, type: 'spring', stiffness: 260, damping: 26 }}
			whileHover={{ y: -6 }}
			className="group relative overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-[0_10px_40px_-26px_rgba(15,23,42,0.35)] hover:shadow-[0_26px_50px_-26px_rgba(90,10,224,0.4)] transition-shadow"
		>
			{/* Gradient cover band */}
			<div className="relative h-[72px]" style={{ background: `linear-gradient(135deg, ${grad[0]}, ${grad[1]})` }}>
				<span aria-hidden className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,.9),transparent_55%)]" />
				<div className="absolute top-2.5 right-2.5">
					<Dropdown placement="bottom-end">
						<DropdownTrigger>
							<Button isIconOnly size="sm" radius="full" aria-label="Channel actions" isLoading={disconnecting}
								className="bg-white/25 text-white backdrop-blur hover:bg-white/40 min-w-8 w-8 h-8">
								<FaEllipsisVertical size={14} />
							</Button>
						</DropdownTrigger>
						<DropdownMenu aria-label="Channel actions">
							{youtubeUrl && (
								<DropdownItem key="open" startContent={<FaArrowUpRightFromSquare size={14} />}
									onPress={() => window.open(youtubeUrl, '_blank', 'noopener')}>
									Open on YouTube
								</DropdownItem>
							)}
							<DropdownItem key="disconnect" className="text-danger" color="danger"
								startContent={<FaLinkSlash size={14} />} onPress={() => onDisconnect(ch.id)}>
								Disconnect
							</DropdownItem>
						</DropdownMenu>
					</Dropdown>
				</div>
			</div>

			<div className="relative z-10 px-[18px] pb-[18px]">
				{/* Avatar overlapping the cover */}
				<div className="-mt-9 mb-3">
					<div className="inline-block rounded-2xl ring-4 ring-white bg-white shadow-sm">
						{ch.thumbnail
							? <img src={ch.thumbnail} alt="" className="block rounded-2xl object-cover" style={{ width: 56, height: 56 }} />
							: <ChannelAvatar ch={{ ...ch, grad }} size={56} />}
					</div>
				</div>

				{/* Title + status on one line */}
				<div className="flex items-center gap-2">
					<span className="min-w-0 truncate text-[15.5px] font-semibold text-slate-800">{ch.channel_title || 'YouTube channel'}</span>
					<span className="flex-none"><YouTubeBadge size={17} /></span>
					<Chip size="sm" variant="flat" radius="full"
						color={needsReconnect ? 'warning' : 'success'}
						className="flex-none ml-auto"
						startContent={
							needsReconnect
								? <span className="ml-1.5"><FaTriangleExclamation size={11} /></span>
								: <span className="relative flex h-2 w-2 ml-1.5">
									<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
									<span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
								</span>
						}>
						{needsReconnect ? 'Reconnect needed' : 'Connected'}
					</Chip>
				</div>
				{ch.channel_handle && <div className="text-[12.5px] text-slate-500 mt-0.5 truncate">{ch.channel_handle}</div>}

				{needsReconnect && (
					<>
						<p className="mt-3 text-[12px] text-amber-600 leading-snug">
							Access expired. Reconnect to keep publishing to this channel.
						</p>
						<Button
							fullWidth size="sm" radius="full" color="warning"
							className="mt-2 font-semibold text-white"
							isLoading={reconnecting}
							startContent={!reconnecting && <FaRotate size={13} />}
							onPress={() => onReconnect(ch.id)}>
							Reconnect
						</Button>
					</>
				)}

				{youtubeUrl && (
					<Button as="a" href={youtubeUrl} target="_blank" rel="noopener"
						fullWidth size="sm" variant="flat" radius="full"
						className={`${needsReconnect ? 'mt-2' : 'mt-4'} font-medium text-slate-600`}
						endContent={<FaArrowUpRightFromSquare size={12} />}>
						Open on YouTube
					</Button>
				)}
			</div>
		</motion.div>
	);
}

/* ---- Empty state ---- */
function ChannelsEmpty({ onConnect, connecting }) {
	return (
		<motion.div
			initial={{ opacity: 0, y: 16 }}
			animate={{ opacity: 1, y: 0 }}
			className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white px-10 py-16 text-center grid place-items-center"
		>
			<span aria-hidden className="pointer-events-none absolute -top-16 left-1/4 h-48 w-48 rounded-full bg-youtube/10 blur-3xl" />
			<span aria-hidden className="pointer-events-none absolute -bottom-16 right-1/4 h-48 w-48 rounded-full bg-primary/10 blur-3xl" />
			<motion.div
				className="relative w-[78px] h-[78px] rounded-[22px] bg-youtube_soft grid place-items-center mb-5"
				animate={{ y: [0, -6, 0] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}>
				<YouTubeBadge size={46} />
			</motion.div>
			<h3 className="relative m-0 mb-2 text-[22px] font-semibold text-slate-800">Connect your first channel</h3>
			<p className="relative text-sm text-slate-500 max-w-[440px]">Sign in with Google and add the YouTube channels you own. Once connected, they're available as sources and destinations everywhere in AudioDub.</p>
			<Button color="primary" size="lg" radius="full" className="relative mt-6" isLoading={connecting} startContent={!connecting && <FaPlus size={16} />} onPress={onConnect}>Connect channel</Button>
		</motion.div>
	);
}

export default function ChannelsPage() {
	const queryClient = useQueryClient();
	const [disconnectingId, setDisconnectingId] = useState(null);

	const channelsQuery = useQuery({
		queryKey: ['channels'],
		queryFn: () => repositories.channel.list(),
	});
	const channels = channelsQuery.data || [];

	// Surface the result of the OAuth round-trip (?channel_connect=success|error).
	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const result = params.get('channel_connect');
		if (!result) return;

		if (result === 'success') {
			const title = params.get('title') || 'Channel';
			const isNew = params.get('new') !== '0';
			if (isNew) {
				toast.success(`${title} connected.`);
			} else {
				toast(`${title} is already connected.`, { icon: 'ℹ️' });
			}
			queryClient.invalidateQueries({ queryKey: ['channels'] });
		} else {
			const reason = params.get('reason');
			toast.error(CONNECT_ERRORS[reason] || 'Could not connect the channel. Please try again.');
		}
		// Strip the query params so a refresh doesn't re-fire the toast.
		window.history.replaceState({}, '', window.location.pathname);
	}, [queryClient]);

	const connect = useMutation({
		mutationFn: () => repositories.channel.youtubeConnect(),
		onSuccess: (data) => {
			if (data?.auth_url) {
				window.location.href = data.auth_url;
			} else {
				toast.error('Could not start the connection. Please try again.');
			}
		},
		onError: () => toast.error('Could not start the connection. Please try again.'),
	});

	const disconnect = useMutation({
		mutationFn: (id) => repositories.channel.disconnect(id),
		onMutate: (id) => setDisconnectingId(id),
		onSuccess: () => {
			toast.success('Channel disconnected.');
			queryClient.invalidateQueries({ queryKey: ['channels'] });
		},
		onError: () => toast.error('Could not disconnect the channel. Please try again.'),
		onSettled: () => setDisconnectingId(null),
	});

	const onConnect = () => connect.mutate();

	return (
		<div className="relative">
			<Header currentPage="channels" />
			<div className="py-6">
				{channelsQuery.isLoading ? (
					<PageLoader />
				) : channels.length === 0 ? (
					<>
						<CommandBanner count={0} onConnect={onConnect} connecting={connect.isPending} />
						<ChannelsEmpty onConnect={onConnect} connecting={connect.isPending} />
					</>
				) : (
					<>
						<CommandBanner count={channels.length} onConnect={onConnect} connecting={connect.isPending} />
						<ProvidersStrip count={channels.length} />
						<div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
							{channels.map((ch, i) => (
								<ConnectedChannelCard
									key={ch.id}
									ch={ch}
									index={i}
									onDisconnect={(id) => disconnect.mutate(id)}
									disconnecting={disconnectingId === ch.id}
									onReconnect={() => connect.mutate()}
									reconnecting={connect.isPending}
								/>
							))}
						</div>
					</>
				)}
			</div>
		</div>
	);
}
